import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '../components/auth/index.js';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { DesignSearchGridSkeleton } from '../components/feedback/DesignSearchGridSkeleton/index.js';
import { useDesignSearch, useDesignThumbnail } from '../hooks/index.js';
import { toResizedThumbnailUrl } from '../lib/design-thumbnail-url.js';
import type {
  DesignSearchFacet,
  DesignSearchResult,
  DesignSearchFacets,
} from '../types/design-search.js';

/** 1 回の検索で取得する上限件数 @graph-connects none */
const SEARCH_LIMIT = 30;

/**
 * 案件ファセットの最下段に固定する airCloset 系（PRO）。
 * ABLE / Talk / Fitting は成果物検索の対象から外したため API がファセットを返さない
 * （product-api の EXCLUDED_CASE_FOLDERS）。
 * @graph-connects none
 */
const CASE_ORDER_BOTTOM = ['airCloset PRO'];
/**
 * 最下段の直上に置く非 airCloset 系（Chill ST / AdobeStock）。
 * @graph-connects none
 */
const CASE_ORDER_MIDDLE = ['Chill ST', 'AdobeStock'];

/**
 * 案件ファセットの表示順の tier を返す（小さいほど上）。その他 airCloset 系(1) → Chill ST/AdobeStock(2)
 * → PRO(3)。同 tier 内は API の並び（件数降順）を stable sort で保つ。
 * @graph-connects none
 */
function caseOrderTier(value: string): number {
  if (CASE_ORDER_BOTTOM.includes(value)) return 3;
  if (CASE_ORDER_MIDDLE.includes(value)) return 2;
  return 1;
}

/**
 * 案件ファセットを tier 順に並べ替える（元配列は変更しない）。
 * @graph-connects none
 */
export function sortCaseFacets(cases: DesignSearchFacet[]): DesignSearchFacet[] {
  return [...cases].sort((a, b) => caseOrderTier(a.value) - caseOrderTier(b.value));
}

/** @graph-connects none */
export const Route = createFileRoute('/design-search')({
  component: DesignSearchRoute,
});

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン成果物検索ページのルート。アクセスガードで包む
 * @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする
 */
function DesignSearchRoute() {
  return (
    <ProtectedRoute pageKey="designSearch">
      <DesignSearchPage />
    </ProtectedRoute>
  );
}

/**
 * 検索フォーム。フィルタバーの右に置くトップバー用の入力＋検索ボタン。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business キーワード入力と検索実行のフォーム
 * @graph-connects none
 */
function SearchForm({
  input,
  onInput,
  onSubmit,
  autoFocus,
}: {
  input: string;
  onInput: (v: string) => void;
  onSubmit: () => void;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex w-full items-center gap-3"
    >
      <div className="flex-1">
        <Input
          type="search"
          value={input}
          onChange={(e) => {
            onInput(e.target.value);
          }}
          placeholder={t('designSearch.placeholder')}
          aria-label={t('designSearch.title')}
          autoFocus={autoFocus}
          fullWidth
          size="md"
        />
      </div>
      <Button type="submit" variant="primary">
        {t('designSearch.searchButton')}
      </Button>
    </form>
  );
}

/**
 * 案件 / 年 の facet 一覧（1 セクション）。空フォルダは API 側で除外済み。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件フォルダ・年でのファセット絞り込み UI（すべて + 各値 + 件数）
 * @graph-connects none
 */
function FacetSection({
  heading,
  facets,
  selected,
  onSelect,
}: {
  heading: string;
  facets: DesignSearchFacet[];
  selected: string | undefined;
  onSelect: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  if (facets.length === 0) {
    return null;
  }
  const rowClass = (active: boolean) =>
    `flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
      active
        ? 'bg-[var(--color-facet-selected-bg)] text-white font-medium'
        : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
    }`;
  return (
    <div className="space-y-1">
      <h2 className="px-2 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
        {heading}
      </h2>
      <button
        type="button"
        onClick={() => {
          onSelect(undefined);
        }}
        className={rowClass(selected === undefined)}
      >
        {t('designSearch.facets.all')}
      </button>
      {facets.map((f) => (
        <button
          key={f.value}
          type="button"
          onClick={() => {
            onSelect(f.value);
          }}
          className={rowClass(selected === f.value)}
        >
          <span className="truncate">{f.value}</span>
          <span className="ml-2 shrink-0 text-xs text-[var(--color-text-muted)]">{f.count}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * 左サイドの案件 breakdown。年/月/種類は上部のプルダウンフィルタへ分離済み。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 検索結果の案件フォルダファセットを左サイドに表示し絞り込みを行う（並びは #1 のカスタム順）
 * @graph-connects none
 */
function FacetSidebar({
  cases,
  caseFolder,
  onCase,
}: {
  cases: DesignSearchFacet[];
  caseFolder: string | undefined;
  onCase: (v: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <aside className="w-full shrink-0 space-y-5 sm:w-56">
      <FacetSection
        heading={t('designSearch.facets.caseHeading')}
        facets={cases}
        selected={caseFolder}
        onSelect={onCase}
      />
      <p className="px-2 text-xs text-[var(--color-text-muted)]">
        {t('designSearch.facets.countHint')}
      </p>
    </aside>
  );
}

/**
 * 年・月・種類のプルダウンフィルタ（検索バーの左に配置）。月は選択年の下の階層として、その年に
 * 属する `YYYY-MM` だけを候補にする。年を変えたら月は親側でクリアする。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 年/月/種類をプルダウンで絞り込むフィルタバー（月は選択年に連動）
 * @graph-connects none
 */
function FilterBar({
  facets,
  year,
  month,
  type,
  onYear,
  onMonth,
  onType,
}: {
  facets: DesignSearchFacets | undefined;
  year: string | undefined;
  month: string | undefined;
  type: string | undefined;
  onYear: (v: string | undefined) => void;
  onMonth: (v: string | undefined) => void;
  onType: (v: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const years = facets?.years ?? [];
  const months = facets?.months ?? [];
  const types = facets?.types ?? [];

  // 月候補は選択年配下（value が `${year}-` で始まるもの）だけに絞る。年未選択なら空。
  const monthOptions: SelectOption[] =
    year !== undefined
      ? months
          .filter((m) => m.value.startsWith(`${year}-`))
          .map((m) => ({
            value: m.value,
            label: t('designSearch.filters.monthLabel', {
              month: Number.parseInt(m.value.slice(5), 10),
            }),
          }))
      : [];

  const yearOptions: SelectOption[] = [
    { value: '', label: t('designSearch.filters.yearAll') },
    ...years.map((y) => ({ value: y.value, label: `${y.value}（${String(y.count)}）` })),
  ];
  const typeOptions: SelectOption[] = [
    { value: '', label: t('designSearch.filters.typeAll') },
    ...types.map((ty) => ({ value: ty.value, label: `${ty.value}（${String(ty.count)}）` })),
  ];
  const monthSelectOptions: SelectOption[] = [
    { value: '', label: t('designSearch.filters.monthAll') },
    ...monthOptions,
  ];

  const toValue = (raw: string): string | undefined => (raw === '' ? undefined : raw);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        size="sm"
        aria-label={t('designSearch.facets.yearHeading')}
        options={yearOptions}
        value={year ?? ''}
        onChange={(e) => {
          onYear(toValue(e.target.value));
        }}
      />
      <Select
        size="sm"
        aria-label={t('designSearch.facets.monthHeading')}
        options={monthSelectOptions}
        value={month ?? ''}
        disabled={year === undefined || monthOptions.length === 0}
        onChange={(e) => {
          onMonth(toValue(e.target.value));
        }}
      />
      <Select
        size="sm"
        aria-label={t('designSearch.facets.typeHeading')}
        options={typeOptions}
        value={type ?? ''}
        onChange={(e) => {
          onType(toValue(e.target.value));
        }}
      />
    </div>
  );
}

/**
 * 成果物サムネイル。GCS 永続コピーを Cookie 認証付き fetch で取得（object URL 化）して優先表示し、
 * 失敗時は配信時リサイズ付きサムネイル → 変換なしの元 URL → プレースホルダー絵文字へ段階フォールバックする。
 *
 * 認証付き fetch にするのは、`<img src>` の直参照だと Edge Router がサブリソースを非認証扱いにして
 * OAuth へ 302 リダイレクトし、閲覧者のセッション状態次第でサムネイルが壊れるため（検索本体と同じ
 * 認証経路に乗せる）。フォールバックは referrer 由来の 403 を避けるため `no-referrer` を付ける。
 *
 * リサイズ URL（`/cdn-cgi/image/...`）が失敗したら変換なしの元 URL に落とすのは、Cloudflare の
 * Image Transformations が zone 設定・プラン依存で、無効なら変換 URL だけが 404 になるため。
 * ここで元 URL に戻さないと、機能が効いていない環境でサムネイルが全滅する（重いが映る、を保つ）。
 *
 * `useDesignThumbnail` が返す `ref` を外枠へ付け、カードが視界に入るまで認証 fetch を遅延する
 * （`<img loading="lazy">` を fetch へ置き換えたことで失われる遅延読み込みの補填）。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 成果物サムネイルを GCS プロキシ→配信時リサイズ→元 URL→絵文字の順に段階フォールバックして描画する
 * @graph-connects useDesignThumbnail [calls] サムネイル GCS プロキシの認証付き取得
 * @graph-connects toResizedThumbnailUrl [calls] 配信時リサイズ付き URL への変換
 */
function ThumbnailPreview({ result }: { result: DesignSearchResult }) {
  const { objectUrl, isError: proxyError, ref } = useDesignThumbnail(result.thumbnailObjectKey);
  const originalSrc = result.thumbnailUrl;
  const resizedSrc = toResizedThumbnailUrl(originalSrc);
  const [resizeFailed, setResizeFailed] = useState(false);
  const [originalFailed, setOriginalFailed] = useState(false);
  const hasProxyKey = result.thumbnailObjectKey != null && result.thumbnailObjectKey !== '';
  const proxyResolved = !hasProxyKey || proxyError;
  // 変換不要（対象ホスト外）なら resizedSrc は元 URL と同値になり、この段は自然にスキップされる。
  const useResized = resizedSrc !== null && resizedSrc !== originalSrc && !resizeFailed;

  // 遅延読み込みゲートの観測対象を常に描画するため、表示内容を包む外枠へ ref を付ける。
  return (
    <div ref={ref} className="flex h-full w-full items-center justify-center">
      {objectUrl !== null ? (
        // 1) GCS プロキシ（認証付き fetch → Blob → object URL）が取れたらそれを表示する。
        <img
          src={objectUrl}
          alt={result.title}
          loading="lazy"
          className="h-full w-full object-contain"
        />
      ) : proxyResolved && useResized ? (
        // 2) プロキシが解決済みなら、まず配信時リサイズ版（実測で転送量 99% 減）を表示する。
        <img
          src={resizedSrc}
          alt={result.title}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="h-full w-full object-contain"
          onError={() => {
            setResizeFailed(true);
          }}
        />
      ) : proxyResolved && originalSrc !== null && !originalFailed ? (
        // 3) リサイズが効かない（zone 設定未有効など）ときは変換なしの元 URL に落とす。
        <img
          src={originalSrc}
          alt={result.title}
          loading="lazy"
          referrerPolicy="no-referrer"
          className="h-full w-full object-contain"
          onError={() => {
            setOriginalFailed(true);
          }}
        />
      ) : (
        // 4) プロキシ取得中（視界外含む）、またはすべて失敗 → プレースホルダー絵文字。
        <span className="text-3xl" aria-hidden="true">
          {result.previewKind === 'video' ? '🎬' : '🖼️'}
        </span>
      )}
    </div>
  );
}

/**
 * 検索結果 1 件のカード（design 成果物のみ）。プレビュー画像 + 案件/年 を表示し、クリックで Drive を開く。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン成果物 1 件をプレビュー付きカードで描画。クリックで Drive の元ファイルを開く
 * @graph-connects none
 */
function ResultCard({ result }: { result: DesignSearchResult }) {
  const { t } = useTranslation();
  const context = [result.caseFolder, result.year, result.type].filter(Boolean).join(' · ');
  return (
    <a
      href={result.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--color-border-focus)]"
      aria-label={t('designSearch.openDesign')}
    >
      <Card className="h-full overflow-hidden transition-shadow group-hover:shadow-md">
        <div className="flex aspect-video w-full items-center justify-center overflow-hidden bg-[var(--color-bg-elevated)]">
          <ThumbnailPreview result={result} />
        </div>
        <CardContent className="space-y-2">
          {context ? (
            <div>
              <Badge variant="info">{context}</Badge>
            </div>
          ) : null}
          <h3 className="truncate font-semibold text-[var(--color-text-primary)]">
            {result.title}
          </h3>
          {result.snippet ? (
            <p className="line-clamp-2 text-sm text-[var(--color-text-secondary)]">
              {result.snippet}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </a>
  );
}

/**
 * デザイン成果物検索ページ本体。中央ヒーロー → 検索後は左ファセット + 成果物グリッド。
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン成果物検索ページ。キーワードで Drive 成果物を横断検索し、案件/年で絞り込みプレビュー表示する。Slack 履歴は結果に出さず recall の隠れ信号として使う
 * @graph-connects useDesignSearch [calls] デザイン成果物検索の実行
 */
function DesignSearchPage() {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [caseFolder, setCaseFolder] = useState<string | undefined>(undefined);
  const [year, setYear] = useState<string | undefined>(undefined);
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [type, setType] = useState<string | undefined>(undefined);

  const { data, isFetching, isError } = useDesignSearch({
    q: submitted,
    caseFolder,
    year,
    month,
    type,
    limit: SEARCH_LIMIT,
  });

  const submit = () => {
    setSubmitted(input.trim());
  };

  // 年を変えたら月は選択解除する（月は選択年に連動するため）。
  const handleYear = (value: string | undefined) => {
    setYear(value);
    setMonth(undefined);
  };

  const results = data?.results ?? [];
  const facets = data?.facets;
  const sortedCases = facets ? sortCaseFacets(facets.cases) : [];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-3">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {t('designSearch.title')}
        </h1>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <FilterBar
            facets={facets}
            year={year}
            month={month}
            type={type}
            onYear={handleYear}
            onMonth={setMonth}
            onType={setType}
          />
          <div className="lg:flex-1">
            <SearchForm input={input} onInput={setInput} onSubmit={submit} autoFocus />
          </div>
        </div>
      </div>

      {isError ? (
        <p className="text-sm text-[var(--color-error)]">{t('designSearch.error')}</p>
      ) : null}

      <div className="flex flex-col gap-6 sm:flex-row">
        {sortedCases.length > 0 ? (
          <FacetSidebar cases={sortedCases} caseFolder={caseFolder} onCase={setCaseFolder} />
        ) : null}

        <div className="min-w-0 flex-1">
          {isFetching ? (
            <DesignSearchGridSkeleton />
          ) : data && !data.embedded ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('designSearch.degraded')}</p>
          ) : results.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('designSearch.noResults')}</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((result) => (
                <ResultCard key={result.id} result={result} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
